import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { FuelDistributionItem } from "services/api/dashboard/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import CardHeader from "../../../../common/CardHeader";
import { text } from "../../../../common/dashboardPalette";

interface VehicleFuelDistributionCardProps {
  data: FuelDistributionItem[];
}

/** Mapiranje kratice goriva na puni naziv */
const FUEL_NAMES: Record<string, string> = {
  D: "Diesel",
  P: "Benzin",
  E: "Elektro",
  H: "Hybrid",
  Diesel: "Diesel",
  Benzin: "Benzin",
  Hybrid: "Hybrid",
  Elektro: "Elektro",
};

/** Dashboard paleta — series boje (sky/indigo/teal/amber/rose) za fuel tipove. */
const FUEL_COLORS: Record<string, string> = {
  Diesel: "#0ea5e9", // sky-500
  Benzin: "#f59e0b", // amber-500
  Hybrid: "#6366f1", // indigo-500
  Elektro: "#10b981", // emerald-500
  D: "#0ea5e9",
  P: "#f59e0b",
  H: "#6366f1",
  E: "#10b981",
};
const DEFAULT_COLOR = "#94a3b8"; // slate-400

// Ispuna trake (okrenut gradient + border na kraju) i hover ring po tipu goriva. Pune klase za Tailwind JIT.
const FUEL_BAR: Record<string, { fill: string; ring: string }> = {
  Diesel: { fill: "bg-gradient-to-l from-sky-500/60 to-sky-500/15 border-r-2 border-sky-500 group-hover/row:from-sky-500/75", ring: "hover:ring-sky-400" },
  Benzin: { fill: "bg-gradient-to-l from-amber-500/60 to-amber-500/15 border-r-2 border-amber-500 group-hover/row:from-amber-500/75", ring: "hover:ring-amber-400" },
  Hybrid: { fill: "bg-gradient-to-l from-indigo-500/60 to-indigo-500/15 border-r-2 border-indigo-500 group-hover/row:from-indigo-500/75", ring: "hover:ring-indigo-400" },
  Elektro: { fill: "bg-gradient-to-l from-emerald-500/60 to-emerald-500/15 border-r-2 border-emerald-500 group-hover/row:from-emerald-500/75", ring: "hover:ring-emerald-400" },
};
const DEFAULT_BAR = { fill: "bg-gradient-to-l from-slate-400/60 to-slate-400/15 border-r-2 border-slate-400 group-hover/row:from-slate-400/75", ring: "hover:ring-slate-400" };

const VehicleFuelDistributionCard: React.FC<VehicleFuelDistributionCardProps> = ({ data }) => {
  const navigate = useNavigate();
  const items = Array.isArray(data) ? data : [];

  // Klik na tip goriva -> pretraga vozila filtrirana po tom gorivu (kod = raw naziv iz distribucije).
  const handleFuelClick = (rawName: string) => {
    const query = rawName && rawName !== "Ostalo" ? `?fuel=${encodeURIComponent(rawName)}` : "";
    navigate(`${FleetVehicleRoutes.FleetVehicleSearchPageRouter}${query}`);
  };
  const total = items.reduce((sum, d) => sum + d.count, 0);

  // Sortirano po veličini, s normaliziranim imenom + bojom + share %.
  const sorted = useMemo(() => {
    return items
      .map((d) => ({
        rawName: d.name,
        name: FUEL_NAMES[d.name] ?? d.name,
        count: d.count,
        color: FUEL_COLORS[d.name] ?? DEFAULT_COLOR,
        share: total > 0 ? (d.count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [items, total]);

  // Eko KPI = % flote koja je elektro/hybrid (uvijek korisno za fleet menadžere).
  const ecoCount = sorted
    .filter((s) => s.name === "Elektro" || s.name === "Hybrid")
    .reduce((sum, s) => sum + s.count, 0);
  const ecoShare = total > 0 ? (ecoCount / total) * 100 : 0;

  if (items.length === 0) {
    return (
      <div className="rounded-sm px-4 py-5 bg-white h-full flex items-center justify-center">
        <span className="text-sm text-slate-400">Nema podataka</span>
      </div>
    );
  }

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title="Gorivo"
        subtitle="Distribucija po tipu goriva"
        className="mb-6"
        right={
          <div className="flex flex-col items-end">
            <span className={`text-2xl font-bold ${text.primary}`}>{total}</span>
            <span className={`text-sm ${text.muted}`}>
              {ecoShare > 0 ? `${ecoShare.toFixed(0)}% eko` : "vozila"}
            </span>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full">
        {/* Donut lijevo — responzivan */}
        <div className="relative flex-shrink-0 w-[140px] h-[140px] sm:w-[180px] sm:h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={sorted}
                dataKey="count"
                nameKey="name"
                innerRadius="62%"
                outerRadius="95%"
                paddingAngle={2}
                stroke="none"
              >
                {sorted.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-slate-900">{total}</span>
            <span className="text-xs text-slate-500">vozila</span>
          </div>
        </div>

        {/* Lista desno — trake s podacima unutar bara */}
        <div className="flex-1 space-y-2 min-w-0 w-full">
          {sorted.map((item) => {
            const bar = FUEL_BAR[item.name] ?? DEFAULT_BAR;
            return (
              <div
                key={item.rawName}
                className={`group/row relative overflow-hidden rounded-sm ring-1 ring-slate-200 bg-white cursor-pointer transition-all hover:ring-2 hover:shadow-sm ${bar.ring}`}
                onClick={() => handleFuelClick(item.rawName)}
              >
                {/* Ispuna proporcionalna udjelu — podaci stoje UNUTAR bara. */}
                <div
                  className={`absolute inset-y-0 left-0 origin-left animate-growRight transition-all duration-500 ${bar.fill}`}
                  style={{ width: `${Math.max(item.share, 5)}%` }}
                />
                <div className="relative flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{item.name}</div>
                    <div className="text-xs text-slate-500">{item.share.toFixed(1)}% udio</div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="text-sm font-bold text-slate-800">{item.count}</span>
                    <span className="text-xs text-slate-500">{item.count === 1 ? "vozilo" : "vozila"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleFuelDistributionCard);
